"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  convertScore,
  ENGLISH_TESTS,
  type EnglishTest,
  type Equivalents,
  IELTS_ROWS,
  isValidScore,
  type Range,
  TOEFL_NEW_BANDS,
} from "@/lib/tool-kit/exam-scores/english-tests";

/** English strings. A translated content file overrides these through `ui`. */
export const ENGLISH_PROFICIENCY_SCORE_CONVERTER_STRINGS = {
  inputHeading: "Your score",
  testLabel: "Test",
  tests: {
    ielts: "IELTS Academic overall band",
    "toefl-old": "TOEFL iBT total, 0–120 (tests before January 2026)",
    "toefl-new": "TOEFL iBT band, 1–6 (from January 2026)",
    pte: "PTE Academic overall score",
    cambridge: "Cambridge English Scale score",
    duolingo: "Duolingo English Test overall score",
  },
  scoreLabel: "Score",
  hints: {
    ielts: "A band from 0 to 9 in half-band steps, such as 6.5.",
    "toefl-old": "A whole number from 0 to 120.",
    "toefl-new": "A band from 1 to 6 in half-band steps, such as 4.5.",
    pte: "A whole number from 10 to 90.",
    cambridge: "A whole number from 80 to 230.",
    duolingo: "A multiple of 5 from 10 to 160.",
  },
  invalid: "That is not a possible score on this scale. See the hint above.",
  belowRange:
    "That score is below the lowest level the published comparison covers, so there is no matching IELTS band.",
  results: "Comparable scores",
  matchSingle: "IELTS Academic band",
  matchRange: "IELTS Academic bands",
  matchAtLeast: "Your score meets the published minimum for IELTS band {band}.",
  matchSpan:
    "Your score covers more than one IELTS band, so the other tests show the full span.",
  matchExact:
    "Each figure below is the published comparison for that IELTS band.",
  colTest: "Test",
  colScore: "Comparable score",
  rowNames: {
    ielts: "IELTS Academic overall band",
    toeflOld: "TOEFL iBT total (tests before January 2026)",
    toeflNew: "TOEFL iBT band, 1–6 (via ETS's own total ranges)",
    pteEquivalent: "PTE Academic overall (IELTS comparison)",
    pteMinimum: "PTE Academic overall, minimum (Pearson)",
    cambridge: "Cambridge English Scale",
    duolingo: "Duolingo English Test overall",
  },
  atLeast: "{value} or higher",
  notPublished: "Not published for this level",
  caveat:
    "These are statistical comparisons published by the test providers, not guarantees. Universities, employers and immigration authorities set their own requirements.",
  toeflNewCaveat:
    "IELTS says TOEFL iBT scores from January 2026 should not be assumed to be directly comparable with its published table, so any 1–6 band here is an indirect estimate.",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "{test} {score} ≈ IELTS Academic {bands}",
  tableHeading: "All published comparisons, by IELTS band",
  colIelts: "IELTS",
  colToefl: "TOEFL iBT (total)",
  colPteEq: "PTE (IELTS comparison)",
  colPteMin: "PTE (Pearson minimum)",
  colCambridge: "Cambridge Scale",
  colDuolingo: "Duolingo",
  dash: "—",
  etsHeading: "ETS: TOEFL iBT 1–6 band and the 0–120 totals it covers",
  colBand: "Band (1–6)",
  colTotals: "Total on the 0–120 scale",
} as const;

type Strings = typeof ENGLISH_PROFICIENCY_SCORE_CONVERTER_STRINGS;
type Format = ReturnType<typeof useToolNumber>;

const ONE_DECIMAL = { minimumFractionDigits: 1, maximumFractionDigits: 1 };
const PLACEHOLDER: Record<EnglishTest, string> = {
  ielts: "6.5",
  "toefl-old": "85",
  "toefl-new": "4.5",
  pte: "60",
  cambridge: "176",
  duolingo: "120",
};

function rangeText(
  range: Range | null,
  fmt: Format,
  strings: Strings,
  options?: Intl.NumberFormatOptions
): string {
  if (range === null) {
    return strings.notPublished;
  }
  const low = fmt(range.min, options);
  return range.min === range.max ? low : `${low}–${fmt(range.max, options)}`;
}

interface Row {
  key: string;
  skip: boolean;
  text: string;
}

function buildRows(
  test: EnglishTest,
  eq: Equivalents,
  strings: Strings,
  fmt: Format
): string[][] {
  const rows: Row[] = [
    {
      key: "ielts",
      skip: test === "ielts",
      text: rangeText(eq.ielts, fmt, strings, ONE_DECIMAL),
    },
    {
      key: "toeflOld",
      skip: test === "toefl-old",
      text: rangeText(eq.toeflOld, fmt, strings),
    },
    {
      key: "toeflNew",
      skip: test === "toefl-new",
      text: rangeText(eq.toeflNew, fmt, strings, ONE_DECIMAL),
    },
    {
      key: "pteEquivalent",
      skip: test === "pte",
      text: rangeText(eq.pteEquivalent, fmt, strings, ONE_DECIMAL),
    },
    {
      key: "pteMinimum",
      skip: test === "pte",
      text: eq.pteMinimum
        ? t(strings.atLeast, {
            value: rangeText(eq.pteMinimum, fmt, strings),
          })
        : strings.notPublished,
    },
    {
      key: "cambridge",
      skip: test === "cambridge",
      text: rangeText(eq.cambridge, fmt, strings),
    },
    {
      key: "duolingo",
      skip: test === "duolingo",
      text: rangeText(eq.duolingo, fmt, strings),
    },
  ];
  return rows
    .filter((row) => !row.skip)
    .map((row) => [
      strings.rowNames[row.key as keyof Strings["rowNames"]],
      row.text,
    ]);
}

function Results({
  test,
  value,
  strings,
  fmt,
}: {
  test: EnglishTest;
  value: number;
  strings: Strings;
  fmt: Format;
}) {
  const conversion = convertScore(test, value);
  if (conversion === null) {
    return <Notice>{strings.belowRange}</Notice>;
  }
  const { match, equivalents: eq } = conversion;
  const bands = rangeText(eq.ielts, fmt, strings, ONE_DECIMAL);
  const multiple = match.bands.length > 1;
  let explanation: string = strings.matchExact;
  if (match.atLeast) {
    explanation = t(strings.matchAtLeast, { band: bands });
  } else if (multiple) {
    explanation = strings.matchSpan;
  }
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={multiple ? strings.matchRange : strings.matchSingle}
          value={bands}
        />
      </StatGrid>
      <p className="text-muted-foreground text-sm">{explanation}</p>
      <DataTable
        head={[strings.colTest, strings.colScore]}
        rows={buildRows(test, eq, strings, fmt)}
      />
      <p className="text-muted-foreground text-sm">{strings.caveat}</p>
      <p className="text-muted-foreground text-sm">{strings.toeflNewCaveat}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={t(strings.summary, {
          test: strings.tests[test],
          score: fmt(value),
          bands,
        })}
      />
    </>
  );
}

function ReferenceTables({ strings, fmt }: { strings: Strings; fmt: Format }) {
  const cell = (value: number | null, options?: Intl.NumberFormatOptions) =>
    value === null ? strings.dash : fmt(value, options);
  return (
    <section className="space-y-6">
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
        <DataTable
          head={[
            strings.colIelts,
            strings.colToefl,
            strings.colPteEq,
            strings.colPteMin,
            strings.colCambridge,
            strings.colDuolingo,
          ]}
          rows={IELTS_ROWS.map((row) => [
            fmt(row.band, ONE_DECIMAL),
            row.toefl[0] === row.toefl[1]
              ? fmt(row.toefl[0])
              : `${fmt(row.toefl[0])}–${fmt(row.toefl[1])}`,
            cell(row.pteEquivalent, ONE_DECIMAL),
            cell(row.pteMinimum),
            cell(row.cambridge),
            row.duolingo.length === 0
              ? strings.dash
              : row.duolingo.map((score) => fmt(score)).join(", "),
          ])}
        />
      </div>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.etsHeading}</h3>
        <DataTable
          head={[strings.colBand, strings.colTotals]}
          rows={TOEFL_NEW_BANDS.map((entry) => [
            fmt(entry.band, ONE_DECIMAL),
            `${fmt(entry.minTotal)}–${fmt(entry.maxTotal)}`,
          ])}
        />
      </div>
    </section>
  );
}

export default function EnglishProficiencyScoreConverterWidget() {
  const strings = useToolUi(ENGLISH_PROFICIENCY_SCORE_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [test, setTest] = useState<EnglishTest>("ielts");
  const [raw, setRaw] = useState("6.5");
  const parsed = raw.trim() === "" ? null : parseNumberInput(raw);
  const valid = isValidScore(test, parsed);

  return (
    <div className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.inputHeading}</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="eps-test"
          label={strings.testLabel}
          onChange={setTest}
          options={ENGLISH_TESTS.map((id) => ({
            value: id,
            label: strings.tests[id],
          }))}
          value={test}
        />
        <TextField
          hint={strings.hints[test]}
          id="eps-score"
          label={strings.scoreLabel}
          numeric
          onChange={setRaw}
          placeholder={PLACEHOLDER[test]}
          value={raw}
        />
      </div>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {raw.trim() !== "" && valid && parsed !== null ? (
          <Results fmt={fmt} strings={strings} test={test} value={parsed} />
        ) : null}
        {raw.trim() !== "" && !valid ? (
          <Notice>{strings.invalid}</Notice>
        ) : null}
        {raw.trim() === "" ? <Notice>{strings.hints[test]}</Notice> : null}
      </section>

      <ReferenceTables fmt={fmt} strings={strings} />
    </div>
  );
}
